
/**
 * Authentication Service
 * Handles user authentication and token management
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from './LoggingService';

/**
 * Auth service provides authentication functionality
 */
class AuthService {
  /**
   * Get the current auth token
   * @returns Promise resolving to the auth token or null if not authenticated
   */
  async getToken(): Promise<string | null> {
    try {
      const { data, error } = await supabase.auth.getSession();
      
      if (error) {
        logger.error('AuthService', 'Error getting session', {
          error: error.message
        });
        return null;
      }
      
      const session = data?.session;
      return session?.access_token || null;
    } catch (err) {
      logger.error('AuthService', 'Exception getting token', {
        error: err instanceof Error ? err.message : String(err)
      });
      return null;
    }
  }
  
  /**
   * Check if user is authenticated
   * @returns Promise resolving to boolean indicating authentication status
   */
  async isAuthenticated(): Promise<boolean> {
    const token = await this.getToken();
    return !!token;
  }
  
  /**
   * Get current user
   * @returns Promise resolving to the user object or null if not authenticated
   */
  async getUser() {
    try {
      const { data, error } = await supabase.auth.getUser();
      
      if (error) {
        logger.error('AuthService', 'Error getting user', {
          error: error.message
        });
        return null;
      }
      
      return data.user;
    } catch (err) {
      logger.error('AuthService', 'Exception getting user', {
        error: err instanceof Error ? err.message : String(err)
      });
      return null;
    }
  }

  /**
   * Refresh the current session
   * @returns Promise resolving to boolean indicating success
   */
  async refreshSession(): Promise<boolean> {
    try {
      logger.info('AuthService', 'Attempting session refresh');
      const { data, error } = await supabase.auth.refreshSession();
      
      if (error) {
        logger.error('AuthService', 'Session refresh failed', {
          error: error.message
        });
        return false;
      }
      
      logger.info('AuthService', 'Session refreshed successfully');
      return true;
    } catch (err) {
      logger.error('AuthService', 'Exception during session refresh', {
        error: err instanceof Error ? err.message : String(err)
      });
      return false;
    }
  }

  /**
   * Check if current token is near expiry (within 5 minutes)
   * @returns Promise resolving to boolean indicating if token needs refresh
   */
  async isTokenNearExpiry(): Promise<boolean> {
    try {
      const { data } = await supabase.auth.getSession();
      const session = data?.session;
      
      if (!session) return true; // No session means expired
      
      const expiresAt = session.expires_at;
      if (!expiresAt) return true; // No expiry time means expired
      
      const expiryTime = new Date(expiresAt * 1000);
      const currentTime = new Date();
      const fiveMinutesFromNow = new Date(currentTime.getTime() + 5 * 60 * 1000);
      
      return expiryTime <= fiveMinutesFromNow;
    } catch (err) {
      logger.error('AuthService', 'Exception checking token expiry', {
        error: err instanceof Error ? err.message : String(err)
      });
      return true; // Assume expired on error
    }
  }
}

export const authService = new AuthService();
